import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ArrowLeft, LockKeyhole, RefreshCw, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { getPartnerTeam, invitePartnerTeamMember, removePartnerTeamMember } from "@/lib/partner-business.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/_authenticated/partner/business/team")({ component: PartnerTeam });

function PartnerTeam() {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["partner-team"], queryFn: () => getPartnerTeam() });

  async function sendInvite(input: { displayName: string; email: string; role: "manager" | "office" | "technician" | "accountant" }) {
    setBusy(true);
    try {
      const result = await invitePartnerTeamMember({
        data: {
          ...input,
          returnUrl: window.location.origin + "/team-invitations",
        },
      });
      if (!result.ok) {
        toast.error(result.error);
        return false;
      }
      toast.success(result.emailSent ? "Team invitation sent" : "Invitation refreshed. Email delivery needs attention.");
      await client.invalidateQueries({ queryKey: ["partner-team"] });
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create team invitation");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const ok = await sendInvite({
      displayName: String(form.get("name")),
      email: String(form.get("email")),
      role: String(form.get("role")) as "manager" | "office" | "technician" | "accountant",
    });
    if (ok) formElement.reset();
  }

  async function remove(memberId: string) {
    setBusy(true);
    try {
      const result = await removePartnerTeamMember({ data: { memberId } });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      toast.success("Team access revoked");
      await client.invalidateQueries({ queryKey: ["partner-team"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not revoke team access");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <AppHeader workspace="partner" />
      <main className="product-page business-page">
        <Link to="/partner/business" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> Business
        </Link>
        <header className="business-page-hero">
          <div>
            <p className="technical-label">BUSINESS TEAM</p>
            <h1>Your people, one operation.</h1>
            <p>Invite employees or subcontractors. Access is linked to their signed-in account and business role.</p>
          </div>
          <Users aria-hidden />
        </header>

        {isLoading ? (
          <p className="business-empty">Loading team…</p>
        ) : isError ? (
          <section className="business-locked">
            <Users />
            <div>
              <h2>We couldn’t load your team</h2>
              <p>Try again. No team access or invitations have been changed.</p>
              <button type="button" onClick={() => refetch()} className="btn-secondary-hero">Try again</button>
            </div>
          </section>
        ) : !data?.entitled ? (
          <section className="business-locked">
            <LockKeyhole />
            <div>
              <h2>Teams is included with Shields Business</h2>
              <p>Unlock shared operations and team calendars with a Business membership.</p>
              <Link to="/plans">Compare Partner plans</Link>
            </div>
          </section>
        ) : (
          <>
            {data.canManage ? (
              <form onSubmit={invite} className="business-invite">
                <Input name="name" aria-label="Name" placeholder="Team member name" required />
                <Input name="email" aria-label="Email" type="email" placeholder="Email address" required />
                <select name="role" aria-label="Role">
                  <option value="technician">Technician</option>
                  <option value="office">Office</option>
                  <option value="manager">Manager</option>
                  <option value="accountant">Accountant</option>
                </select>
                <Button disabled={busy}>{busy ? "Sending…" : "Invite member"}</Button>
              </form>
            ) : (
              <p className="business-empty">Your team role can view this roster but cannot change team access.</p>
            )}

            <section className="business-grid">
              {data.members.map((member: { id: string; status: string; display_name: string; email: string; business_role: string }) => (
                <article key={member.id} className="business-record">
                  <span className="business-date">{member.status}</span>
                  <strong>{member.display_name}</strong>
                  <small>{member.email} · {member.business_role}</small>
                  {data.canManage && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {member.status === "invited" && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => sendInvite({
                            displayName: member.display_name,
                            email: member.email,
                            role: member.business_role as "manager" | "office" | "technician" | "accountant",
                          })}
                          className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-border px-3 text-xs font-bold"
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> Resend
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(member.id)}
                        disabled={busy}
                        aria-label={"Revoke access for " + member.display_name}
                        className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-border px-3 text-xs font-bold text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Revoke
                      </button>
                    </div>
                  )}
                </article>
              ))}
              {!data.members.length && <div className="business-empty"><strong>No team members yet</strong><span>Invite your first team member above when you are ready to share business operations.</span></div>}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
